import appConfig from "@core/config";
import { bearer } from "@core/utils";
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { type NavigateFunction } from "react-router-dom";

export enum ErrorType {
  Error,
  ValidationError,
}

export type ResponseError = {
  errorType: ErrorType;
  key: string;
  message: string;
};

export type ServiceResponse<TResultObject = unknown> = {
  errorCount: number;
  errors: ResponseError[];
  message: string;
  resultObject: TResultObject | null;
  status: number;
};

export type TokenHandlers = {
  getToken: () => Promise<string | undefined>;
  getCcauToken: () => Promise<string | undefined>;
};

export class ServerError<TResultObject = unknown> extends Error {
  public readonly responseData: ServiceResponse<TResultObject>;
  protected readonly statusCode: number;

  constructor(response: AxiosResponse<ServiceResponse<TResultObject>>) {
    super(
      `${response.status} Error occured during HTTP ${response.request.method} request to URL(${response.request.url})`,
    );
    this.statusCode = response.status;
    this.responseData = response.data;
  }
}

export default class AxiosService {
  private readonly service: AxiosInstance;

  private readonly getToken: () => Promise<string | undefined>;
  private readonly getCcauToken: () => Promise<string | undefined>;

  private readonly navigateTo: NavigateFunction;

  constructor(
    controller: string,
    { getToken, getCcauToken }: TokenHandlers,
    navigateTo: NavigateFunction,
    version: string = "v1",
  ) {
    this.service = axios.create({
      baseURL: `${appConfig.BASE_URL}/api/${version}/${controller}/`,
      withCredentials: false,
    });

    this.getToken = getToken;
    this.getCcauToken = getCcauToken;
    this.navigateTo = navigateTo;
  }

  private async getConfig(config?: AxiosRequestConfig<unknown>) {
    const token = await this.getToken();
    const ccauToken = await this.getCcauToken();

    const defaultConfig: AxiosRequestConfig = {
      withCredentials: false,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: token ? bearer(token) : undefined,
        "CCAU-Authorization": ccauToken ? bearer(ccauToken) : undefined,
      },
    };

    if (typeof config !== "object") {
      return defaultConfig;
    }

    return Object.assign(defaultConfig, config, {
      headers: {
        ...defaultConfig.headers,
        ...config.headers,
      },
    });
  }

  protected async get<TResponse, TParams = unknown>(
    url: string,
    config?: AxiosRequestConfig,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    const cfg = await this.getConfig(config);
    return this.tryCatchHandler(
      () =>
        this.service.get<
          TResponse,
          AxiosResponse<ServiceResponse<TResponse>>,
          TParams
        >(url, cfg),
      { skipNavigateIfError },
    );
  }

  protected async nativeGet<TResponse, TParams = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ) {
    const cfg = await this.getConfig(config);
    return this.service.get<TResponse, AxiosResponse<TResponse>, TParams>(
      url,
      cfg,
    );
  }

  protected async post<TResponse, TRequest>(
    url: string,
    data: TRequest,
    config?: AxiosRequestConfig,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    const cfg = await this.getConfig(config);
    return this.tryCatchHandler(
      () =>
        this.service.post<
          TResponse,
          AxiosResponse<ServiceResponse<TResponse>>,
          TRequest
        >(url, data, cfg),
      { skipNavigateIfError },
    );
  }

  protected async put<TResponse, TRequest>(
    url: string,
    data: TRequest,
    config?: AxiosRequestConfig,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    const cfg = await this.getConfig(config);
    return this.tryCatchHandler(
      () =>
        this.service.put<
          TResponse,
          AxiosResponse<ServiceResponse<TResponse>>,
          TRequest
        >(url, data, cfg),
      { skipNavigateIfError },
    );
  }

  protected async delete<TResponse>(
    url: string,
    config?: AxiosRequestConfig,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    const cfg = await this.getConfig(config);
    return this.tryCatchHandler(
      () =>
        this.service.delete<
          TResponse,
          AxiosResponse<ServiceResponse<TResponse>>,
          unknown
        >(url, cfg),
      { skipNavigateIfError },
    );
  }

  protected async patch<TResponse, TRequest>(
    url: string,
    data: TRequest,
    config?: AxiosRequestConfig,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    const cfg = await this.getConfig(config);
    return this.tryCatchHandler(
      () =>
        this.service.patch<
          TResponse,
          AxiosResponse<ServiceResponse<TResponse>>,
          TRequest
        >(url, data, cfg),
      { skipNavigateIfError },
    );
  }

  private async tryCatchHandler<TResponse>(
    tryCallback: () => Promise<AxiosResponse<ServiceResponse<TResponse>>>,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    try {
      const response = await tryCallback();
      return this.handleResponse(response, { skipNavigateIfError });
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        return this.handleResponse(
          err.response as AxiosResponse<ServiceResponse<TResponse>>,
          { skipNavigateIfError },
        );
      }

      return undefined as TResponse;
    }
  }

  private handleResponse<TResponse>(
    response: AxiosResponse<ServiceResponse<TResponse>>,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    const data = this.handleResponseStatusCode(response, {
      skipNavigateIfError,
    });
    if (!data) return undefined as TResponse;

    if (data.errorCount > 0) throw new ServerError(response);

    return data.resultObject;
  }

  private handleResponseStatusCode<TResponse>(
    response: AxiosResponse<ServiceResponse<TResponse>>,
    { skipNavigateIfError = false }: { skipNavigateIfError?: boolean } = {},
  ) {
    if (skipNavigateIfError) {
      return response.data;
    }
    switch (response.status) {
      case 401: {
        this.navigateTo("/");
        return response.data;
      }
      case 403: {
        this.navigateTo("/access-denied");
        return response.data;
      }
      case 404: {
        this.navigateTo("/not-found");
        return response.data;
      }
      default: {
        if (
          (response.status.toString().startsWith("5") ||
            response.status.toString().startsWith("4")) &&
          !response.request.responseURL.includes("assign")
        ) {
          this.navigateTo("/error");
          return;
        }

        return response.data;
      }
    }
  }
}
